import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';

import { Inventory } from '../../models/inventory.model';
import { InventoryStatus } from '../../models/inventory-status.enum';
import { InventoryService } from '../../services/inventory.service';

/**
 * FV-243 AC: the `Offen` chip means "alle nicht abgeschlossenen Inventuren",
 * not only rows whose status equals `InventoryStatus.Offen`.
 */
type StatusFilter = 'ALLE' | 'KRITISCH' | InventoryStatus;

type SortColumn =
  | 'prioritaet'
  | 'filiale'
  | 'status'
  | 'wert'
  | 'restzeit'
  // V2: | 'differenz'
  ;

type SortDirection = 'asc' | 'desc';

interface SortState {
  /** null is the third sort state: back to the default prioritisation order. */
  readonly column: SortColumn | null;
  readonly direction: SortDirection;
}

const ALLE_FILIALEN = 'ALLE';

/**
 * Presentation-only prioritisation weights.
 *
 * The authoritative prioritisation rule is backend scope (BE FV-237) and the
 * concrete thresholds are still business-open (FV-236 §12 Q2). These constants
 * only control the default row order of the overview and are intentionally kept
 * private to this component so they are not treated as a product rule.
 */
const RESTZEIT_UEBERFAELLIG_MINUTEN = 0;
const RESTZEIT_KNAPP_MINUTEN = 60;
const RESTZEIT_WARNUNG_MINUTEN = 120;
const MINUTEN_PRO_STUNDE = 60;
const STUNDEN_PRO_TAG = 24;
// V2: const DIFFERENZ_ESKALATION = 2000;
// V2: const ABWEICHUNGEN_ESKALATION = 3;

@Component({
  selector: 'app-inventur-uebersicht',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './inventur-uebersicht.component.html',
  styleUrl: './inventur-uebersicht.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventurUebersichtComponent {
  private readonly inventoryService = inject(InventoryService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  protected readonly InventoryStatus = InventoryStatus;

  protected readonly inventories = signal<Inventory[]>([]);

  protected readonly selectedFiliale = signal(ALLE_FILIALEN);
  protected readonly selectedFilter = signal<StatusFilter>(
    InventoryStatus.Offen
  );
  protected readonly isFilialeDropdownOpen = signal(false);

  protected readonly sortState = signal<SortState>({
    column: 'prioritaet',
    direction: 'asc',
  });

  protected readonly isLoading = signal(true);
  protected readonly skeletonSummaryCards = Array.from({ length: 4 });
  protected readonly skeletonFilterTabs = Array.from({ length: 6 });
  protected readonly skeletonRows = Array.from({ length: 6 });

  protected readonly filialen = computed(() => {
    const filialenMap = new Map<string, string>();

    for (const inventur of this.inventories()) {
      filialenMap.set(String(inventur.filialNummer), inventur.filialeName);
    }

    return [
      { value: ALLE_FILIALEN, label: 'Alle Filialen' },
      ...Array.from(filialenMap.entries()).map(([value, name]) => ({
        value,
        label: name ? `${name} (${value})` : `Filiale ${value}`,
      })),
    ];
  });

  protected readonly selectedFilialeLabel = computed(
    () =>
      this.filialen().find(
        (filiale) => filiale.value === this.selectedFiliale()
      )?.label ?? 'Alle Filialen'
  );

  protected readonly filialeFilteredInventories = computed(() => {
    const selectedFiliale = this.selectedFiliale();

    if (selectedFiliale === ALLE_FILIALEN) {
      return this.inventories();
    }

    return this.inventories().filter(
      (inventur) => String(inventur.filialNummer) === selectedFiliale
    );
  });

  protected readonly sortedInventories = computed(() => {
    const gefiltert = this.filialeFilteredInventories().filter((inventur) =>
      this.matchesFilter(inventur, this.selectedFilter())
    );

    const { column, direction } = this.sortState();
    const factor = direction === 'asc' ? 1 : -1;

    return [...gefiltert].sort((first, second) => {
      switch (column) {
        // Cleared sort falls back to the default prioritisation order.
        case null:
        case 'prioritaet':
          return (this.calcPriority(first) - this.calcPriority(second)) * factor;
        case 'filiale':
          return (first.filialNummer - second.filialNummer) * factor;
        case 'status':
          return first.status.localeCompare(second.status) * factor;
        case 'wert':
          return (second.inventurWert - first.inventurWert) * factor;
        case 'restzeit':
          return (first.restzeitMinuten - second.restzeitMinuten) * factor;
        default:
          return 0;
        // V2: case 'differenz':
        //   return (second.differenzWert - first.differenzWert) * factor;
      }
    });
  });

  protected readonly kritischCount = computed(
    () => this.inventories().filter((entry) => entry.istKritisch).length
  );

  protected readonly offenCount = computed(
    () =>
      this.inventories().filter(
        (entry) => entry.status !== InventoryStatus.Abgeschlossen
      ).length
  );

  // V2: protected readonly gesamtDifferenz = computed(() =>
  //   this.inventories().reduce((sum, entry) => sum + entry.differenzWert, 0)
  // );
  //
  // protected readonly hoheAbweichungenCount = computed(() =>
  //   this.inventories().reduce((sum, entry) => sum + entry.hoheAbweichungen, 0)
  // );

  protected readonly inBearbeitungCount = computed(
    () =>
      this.inventories().filter(
        (entry) => entry.status === InventoryStatus.InBearbeitung
      ).length
  );

  protected readonly totalCount = computed(() => this.inventories().length);

  protected readonly abgeschlossenCount = computed(
    () =>
      this.inventories().filter(
        (entry) => entry.status === InventoryStatus.Abgeschlossen
      ).length
  );

  protected readonly erledigtCount = computed(() => this.abgeschlossenCount());

  protected readonly statusFilters = computed(() => {
    const base = this.filialeFilteredInventories();

    const countByStatus = (status: InventoryStatus): number =>
      base.filter((entry) => entry.status === status).length;

    return [
      {
        label: 'Kritisch',
        value: 'KRITISCH' as const,
        count: base.filter((entry) => entry.istKritisch).length,
      },
      {
        label: 'Offen',
        value: InventoryStatus.Offen,
        count: base.filter(
          (entry) => entry.status !== InventoryStatus.Abgeschlossen
        ).length,
      },
      {
        label: 'In Bearbeitung',
        value: InventoryStatus.InBearbeitung,
        count: countByStatus(InventoryStatus.InBearbeitung),
      },
      {
        label: 'Abgeschlossen',
        value: InventoryStatus.Abgeschlossen,
        count: countByStatus(InventoryStatus.Abgeschlossen),
      },
      {
        label: 'Alle',
        value: 'ALLE' as const,
        count: base.length,
      },
    ];
  });

  constructor() {
    this.isLoading.set(true);

    this.inventoryService
      .getInventories()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (inventories) => {
          this.inventories.set(inventories);
        },
        error: () => {
          this.inventories.set([]);
        },
      });
  }

  @HostListener('document:click', ['$event'])
  protected closeFilialeDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isFilialeDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeFilialeDropdownOnEscape(): void {
    this.isFilialeDropdownOpen.set(false);
  }

  protected toggleFilialeDropdown(): void {
    this.isFilialeDropdownOpen.update((state) => !state);
  }

  protected selectFiliale(filialeValue: string): void {
    this.selectedFiliale.set(filialeValue);
    this.isFilialeDropdownOpen.set(false);
  }

  protected setFilter(filter: StatusFilter): void {
    this.selectedFilter.set(filter);
  }

  protected isFilterActive(filter: StatusFilter): boolean {
    return this.selectedFilter() === filter;
  }

  protected setSort(column: SortColumn): void {
    const current = this.sortState();

    if (current.column !== column) {
      this.sortState.set({ column, direction: 'asc' });

      return;
    }

    if (current.direction === 'asc') {
      this.sortState.set({ column, direction: 'desc' });

      return;
    }

    this.sortState.set({ column: null, direction: 'asc' });
  }

  protected isSortedBy(column: SortColumn): boolean {
    return this.sortState().column === column;
  }

  protected getSortIcon(column: SortColumn): string {
    if (!this.isSortedBy(column)) {
      return 'unfold_more';
    }

    return this.sortState().direction === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  protected openInventory(inventur: Inventory): void {
    this.router.navigate([
      '/inventur',
      inventur.id,
      'filial',
      inventur.filialNummer,
    ]);
  }

  protected startInventory(inventur: Inventory): void {
    this.router.navigate(
      [
        '/inventur',
        inventur.id,
        'filial',
        inventur.filialNummer,
      ],
      {
        queryParams: {
          action: this.getActionKey(inventur),
        },
      }
    );
  }

  protected getStatusLabel(status: InventoryStatus): string {
    switch (status) {
      case InventoryStatus.Offen:
        return 'Offen';
      case InventoryStatus.InBearbeitung:
        return 'In Bearbeitung';
      case InventoryStatus.Erfasst:
        return 'Erfasst';
      case InventoryStatus.Abgeschlossen:
        return 'Abgeschlossen';
      // V2: case InventoryStatus.Ueberfaellig:
      //   return 'Überfällig';
    }
  }

  protected getStatusClass(status: InventoryStatus): string {
    switch (status) {
      case InventoryStatus.Offen:
        return 'status-offen';
      case InventoryStatus.InBearbeitung:
        return 'status-in-bearbeitung';
      case InventoryStatus.Erfasst:
        return 'status-erfasst';
      case InventoryStatus.Abgeschlossen:
        return 'status-abgeschlossen';
      // V2: case InventoryStatus.Ueberfaellig:
      //   return 'status-ueberfaellig';
    }
  }

  protected getActionLabel(inventur: Inventory): string {
    switch (inventur.status) {
      case InventoryStatus.Offen:
        return 'Erfassen';
      case InventoryStatus.Abgeschlossen:
        return 'Ansehen';
      case InventoryStatus.InBearbeitung:
      case InventoryStatus.Erfasst:
      // V2: case InventoryStatus.Ueberfaellig:
        return 'Weiter';
    }
  }

  protected getFortschrittProzent(inventur: Inventory): number {
    if (inventur.anzahlArtikel <= 0) {
      return 0;
    }

    return Math.round(
      (inventur.erfassteArtikel / inventur.anzahlArtikel) * 100
    );
  }

  protected getFortschrittText(inventur: Inventory): string {
    if (inventur.status === InventoryStatus.Abgeschlossen) {
      return 'Fertig';
    }

    return `${inventur.erfassteArtikel}/${inventur.anzahlArtikel}`;
  }

  // V2: /**
  //  * FV-238: `—` is the fallback for "keine Differenz" (0). Negative difference
  //  * values are rendered and must not be hidden; their final presentation is a
  //  * still-open business question (FV-236 §12 Q10).
  //  */
  // protected getDifferenzText(inventur: Inventory): string {
  //   if (inventur.differenzWert === 0) {
  //     return '—';
  //   }
  //
  //   return `${this.formatWert(inventur.differenzWert)} €`;
  // }
  //
  // protected getAbweichungenText(inventur: Inventory): string {
  //   if (inventur.hoheAbweichungen === 0) {
  //     return '—';
  //   }
  //
  //   return String(inventur.hoheAbweichungen);
  // }

  protected getInventurWertText(inventur: Inventory): string {
    return `${inventur.inventurWert.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  }

  protected getRestzeitText(inventur: Inventory): string {
    if (
      inventur.status === InventoryStatus.Abgeschlossen ||
      !Number.isFinite(inventur.restzeitMinuten) ||
      inventur.restzeitMinuten >= Number.MAX_SAFE_INTEGER
    ) {
      return '—';
    }

    if (inventur.restzeitMinuten <= RESTZEIT_UEBERFAELLIG_MINUTEN) {
      return 'ÜBERFÄLLIG';
    }

    const stunden = Math.floor(inventur.restzeitMinuten / MINUTEN_PRO_STUNDE);
    const minuten = inventur.restzeitMinuten % MINUTEN_PRO_STUNDE;
    const minutenText = String(minuten).padStart(2, '0');

    if (stunden >= STUNDEN_PRO_TAG) {
      const tage = Math.floor(stunden / STUNDEN_PRO_TAG);
      const restStunden = stunden % STUNDEN_PRO_TAG;

      return `${tage}T ${String(restStunden).padStart(2, '0')}:${minutenText}`;
    }

    return `${String(stunden).padStart(2, '0')}:${minutenText}`;
  }

  protected isRestzeitKritisch(inventur: Inventory): boolean {
    return (
      inventur.status !== InventoryStatus.Abgeschlossen &&
      inventur.restzeitMinuten <= RESTZEIT_KNAPP_MINUTEN
    );
  }

  protected isRestzeitWarnung(inventur: Inventory): boolean {
    return (
      inventur.status !== InventoryStatus.Abgeschlossen &&
      inventur.restzeitMinuten > RESTZEIT_KNAPP_MINUTEN &&
      inventur.restzeitMinuten <= RESTZEIT_WARNUNG_MINUTEN
    );
  }

  // V2: protected isAbweichungEskaliert(inventur: Inventory): boolean {
  //   return inventur.hoheAbweichungen > ABWEICHUNGEN_ESKALATION;
  // }

  protected isFortschrittGering(inventur: Inventory): boolean {
    return (
      inventur.status !== InventoryStatus.Abgeschlossen &&
      this.getFortschrittProzent(inventur) <= 50
    );
  }

  private getActionKey(inventur: Inventory): string {
    switch (inventur.status) {
      case InventoryStatus.Offen:
        return 'erfassen';
      case InventoryStatus.Abgeschlossen:
        return 'ansehen';
      case InventoryStatus.InBearbeitung:
      case InventoryStatus.Erfasst:
      // V2: case InventoryStatus.Ueberfaellig:
        return 'Weiter';
    }
  }

  // V2: protected isDifferenzKritisch(inventur: Inventory): boolean {
  //   return (
  //     inventur.istKritisch &&
  //     inventur.status !== InventoryStatus.Abgeschlossen &&
  //     inventur.differenzWert !== 0
  //   );
  // }

  protected formatWert(wert: number): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(wert);
  }

  private matchesFilter(inventur: Inventory, filter: StatusFilter): boolean {
    if (filter === 'ALLE') {
      return true;
    }

    if (filter === 'KRITISCH') {
      return inventur.istKritisch;
    }

    if (filter === InventoryStatus.Offen) {
      return inventur.status !== InventoryStatus.Abgeschlossen;
    }

    return inventur.status === filter;
  }

  private calcPriority(inventur: Inventory): number {
    if (inventur.status === InventoryStatus.Abgeschlossen) {
      return 9999;
    }

    let score = 0;

    // V2: the Restzeit and Überfällig weights.
    // if (inventur.restzeitMinuten <= RESTZEIT_UEBERFAELLIG_MINUTEN) {
    //   score -= 1000;
    // } else if (inventur.restzeitMinuten <= RESTZEIT_KNAPP_MINUTEN) {
    //   score -= 500;
    // } else if (inventur.restzeitMinuten <= RESTZEIT_WARNUNG_MINUTEN) {
    //   score -= 300;
    // }
    //
    // if (inventur.status === InventoryStatus.Ueberfaellig) {
    //   score -= 800;
    // }

    if (inventur.status === InventoryStatus.Offen) {
      score -= 400;
    }

    // V2: the deviation weights.
    // if (inventur.differenzWert > DIFFERENZ_ESKALATION) {
    //   score -= 600;
    // }
    //
    // if (inventur.hoheAbweichungen > ABWEICHUNGEN_ESKALATION) {
    //   score -= 200;
    // }
    //
    // score -= inventur.differenzWert / 10;

    return score;
  }
}
